import { AuthUtils, CostCenter, FlowjoCommons, LicenseUtils } from '@portal/portal-core';
import React, { useState, type ComponentType, type ReactElement } from 'react';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import {
  FjTableCell,
  FjTooltip,
  FjTableRow as Row,
  FjTable as Table,
} from 'components/primitives';
import { PRODUCT_INFO } from 'constants/products';
import {
  selectAllProductLicenses,
  selectAllProductsLicensesGrouped,
  selectCostCenters,
  selectProductsLicenseInfo,
  selectSelf,
  selectUsers,
} from 'selectors';
import { formatDate } from 'utils';

// Minimal shapes of the redux-derived values accessed in this component. The
// backing selectors are untyped domain boundaries (analyzed license objects, cost
// centers and user records), so only the fields read here are described.
interface SelfUser {
  id: string;
}

interface CostCenterInfo {
  name?: string;
  owner?: string;
  countryCode?: string;
}

interface DrawerUser {
  firstname?: string;
  lastname?: string;
  primaryEmail?: string;
}

interface LicenseUserSeat {
  suspended?: boolean;
  startDate?: string;
  endDate?: string;
}

interface FullLicenseInfo {
  users?: Record<string, LicenseUserSeat>;
  activityState?: string;
  startDate?: string;
  endDate?: string;
}

interface LicenseStatus {
  isActive: boolean;
  components: string[];
  displayText: string;
  userSeatEndDate?: string;
  licenseEndDate?: string;
}

// Shape of a single grouped/analyzed license entry (untyped selector boundary),
// describing only the fields read when rendering additional licenses.
interface LicenseEntry {
  id: string;
  type?: string;
  costCenterType?: string;
  costCenterId: string;
  activityState?: string;
  authorizedDevicesOverride?: boolean;
}

// FjTableCell requires `_rowIndex`/`_colIndex`, but the parent FjTable injects
// those at runtime via cloneElement; widen the prop type so call sites here need
// not supply them.
interface CellProps {
  span?: string;
  className?: string;
  children?: React.ReactNode;
}
const Cell = FjTableCell as unknown as ComponentType<CellProps>;

// @TODO: Content is not final
const LicenseDrawer = (): ReactElement => {
  const theme = useTheme();
  const licenseInfoArray = useSelector(selectProductsLicenseInfo);
  const allLicensesGrouped = useSelector(selectAllProductsLicensesGrouped);
  const self = useSelector(selectSelf) as SelfUser | undefined;
  const allProductLicenses = useSelector(selectAllProductLicenses);
  const costCenters = useSelector(selectCostCenters) as Record<string, CostCenterInfo> | undefined;
  const users = useSelector(selectUsers) as Record<string, DrawerUser> | undefined;

  // Track which products have their additional licenses expanded
  const [expandedProducts, setExpandedProducts] = useState<Record<string, boolean>>({});

  const tableStyle = {
    // minmax(0, 1fr), not auto - this table sits in a fixed-width (600px) drawer and its middle
    // column can carry long content (e.g. an owner's name + email), so it needs to be able to
    // shrink/wrap instead of forcing the table wider than the drawer.
    gridTemplateColumns: 'max-content minmax(0, 1fr) 150px',
    fontSize: '14px',
    borderColor: theme.colors.selected,
    rowHeight: '50px',
  };

  const toggleProductExpansion = (productName: string): void => {
    setExpandedProducts((prevState) => ({
      ...prevState,
      [productName]: !prevState[productName],
    }));
  };

  const calculateLicenseStatus = (
    productLicenseInfo: FullLicenseInfo,
  ): LicenseStatus | null => {
    if (!productLicenseInfo || !self) return null;

    const userLicense = productLicenseInfo.users?.[self.id];
    const components: string[] = [];

    // Check user seat status
    const isUserSeatActive = userLicense
      ? AuthUtils.isUserSeatActive(
          userLicense.suspended,
          userLicense.startDate,
          userLicense.endDate,
        )
      : false;

    if (!isUserSeatActive) {
      if (userLicense?.suspended) {
        components.push('User Suspended');
      } else if (userLicense?.endDate && new Date(userLicense.endDate) < new Date()) {
        components.push('User Seat Expired');
      } else if (userLicense?.startDate && new Date(userLicense.startDate) > new Date()) {
        components.push('User Seat Not Started');
      } else {
        components.push('User Seat Inactive');
      }
    } else {
      components.push('User Seat Active');
    }

    // Check product access status
    const isProductAccessActive = LicenseUtils.isProductAccessActive(productLicenseInfo);

    if (!isProductAccessActive) {
      if (productLicenseInfo.activityState !== 'active') {
        components.push(`License ${productLicenseInfo.activityState || 'Inactive'}`);
      } else if (productLicenseInfo.endDate && new Date(productLicenseInfo.endDate) < new Date()) {
        components.push('License Expired');
      } else if (
        productLicenseInfo.startDate &&
        new Date(productLicenseInfo.startDate) > new Date()
      ) {
        components.push('License Not Started');
      } else {
        components.push('License Inactive');
      }
    } else {
      components.push('License Active');
    }

    const isActive = isUserSeatActive && isProductAccessActive;

    return {
      isActive,
      components,
      displayText: isActive ? 'Active' : 'Inactive',
      userSeatEndDate: userLicense?.endDate,
      licenseEndDate: productLicenseInfo.endDate,
    };
  };

  const renderProductTables = (): ReactElement | ReactElement[] => {
    if (licenseInfoArray && licenseInfoArray.length > 0) {
      const licenseTableUIArray: ReactElement[] = [];

      licenseInfoArray.forEach((license) => {
        const {
          productName,
          type,
          authorizedDevicesOverride,
          costCenterType,
          costCenterId,
          activityState,
        } = license;
        const trialPending =
          activityState === CostCenter.ACTIVITY_STATES.pending ? ' (Pending)' : '';
        const product = PRODUCT_INFO[productName];
        const { name } = product;
        const activationProductName = productName.toLowerCase().includes('flowjo')
          ? 'FlowJo 11'
          : name;
        // PRODUCT_INFO types Icon as a bare ComponentType|null; the concrete icon
        // components accept a `size` prop.
        const Icon = product.Icon as ComponentType<{ size?: string }>;
        const { eula } = product.links;
        const { manageDevices } = product.paths!;

        // Get full license info for status calculation
        const fullLicenseInfo = allProductLicenses?.[productName]?.find(
          (lic: FullLicenseInfo & { id: unknown }) => lic.id === license.id,
        );
        const licenseStatus = fullLicenseInfo ? calculateLicenseStatus(fullLicenseInfo) : null;

        // Get all licenses for this product
        const allLicensesForProduct = allLicensesGrouped?.[productName] || [];
        const hasAdditionalLicenses = allLicensesForProduct.length > 1;
        const isExpanded = expandedProducts[productName];
        const additionalLicenses = hasAdditionalLicenses ? allLicensesForProduct.slice(1) : [];

        // Build table rows conditionally to avoid passing null children to FjTable
        const tableRows: ReactElement[] = [
          <Row key="cost-center">
            <LabelCell>Cost Center:</LabelCell>
            <Cell>
              {costCenterType === 'institutional'
                ? `${costCenters?.[costCenterId]?.name || 'N/A'}`
                : 'Individual'}
            </Cell>
            <Cell />
          </Row>,
          <Row key="owner">
            <LabelCell>Owner:</LabelCell>
            <Cell>
              {(() => {
                const costCenter = costCenters?.[costCenterId];
                const ownerUserId = costCenter?.owner;
                const ownerUser = ownerUserId ? users?.[ownerUserId] : undefined;
                if (ownerUser) {
                  const { firstname, lastname, primaryEmail } = ownerUser;
                  return (
                    `${firstname || ''} ${lastname || ''}`.trim() +
                    (primaryEmail ? ` (${primaryEmail})` : '')
                  );
                }
                return 'N/A';
              })()}
            </Cell>
            <Cell />
          </Row>,
          <Row key="license-type">
            <LabelCell>License Type:</LabelCell>
            <Cell>
              {type}{' '}
              <FjTooltip Component={<>Activate by Signing in to {activationProductName}</>}>
                {` ${trialPending}`}
              </FjTooltip>
            </Cell>
            <Cell />
          </Row>,
          <Row key="eula">
            <LabelCell>EULA:</LabelCell>
            <Cell>
              <a rel="noopener noreferrer" target="_blank" href={eula}>
                View
              </a>
            </Cell>
            <Cell />
          </Row>,
          <Row key="status">
            <LabelCell>Status:</LabelCell>
            <Cell span="*">
              {licenseStatus ? (
                <span
                  style={{
                    color: licenseStatus.isActive ? 'green' : '#d32f2f',
                    fontWeight: 'bold',
                  }}
                >
                  {licenseStatus.displayText}
                </span>
              ) : (
                'N/A'
              )}
            </Cell>
          </Row>,
          <Row key="license-expiration">
            <LabelCell>
              {costCenterType === 'institutional' ? 'Group License Expiration:' : 'Expiration:'}
            </LabelCell>
            <Cell>
              {licenseStatus?.licenseEndDate ? formatDate(licenseStatus.licenseEndDate) : 'N/A'}
            </Cell>
            <Cell />
          </Row>,
        ];

        // Only show user seat expiration for institutional licenses
        if (costCenterType === 'institutional') {
          tableRows.push(
            <Row key="user-seat-expiration">
              <LabelCell>User Seat Expiration:</LabelCell>
              <Cell>
                {licenseStatus?.userSeatEndDate
                  ? formatDate(licenseStatus.userSeatEndDate)
                  : 'not set'}
              </Cell>
              <Cell />
            </Row>,
          );
        }

        // Add country row
        tableRows.push(
          <Row key="country">
            <LabelCell>Country:</LabelCell>
            <Cell>
              {(() => {
                const countryCode = costCenterId && costCenters?.[costCenterId]?.countryCode;
                if (!countryCode) return 'N/A';
                const countryName = FlowjoCommons.Regional.getCountryName(countryCode);
                return countryName ? `${countryName} (${countryCode})` : countryCode;
              })()}
            </Cell>
            <Cell />
          </Row>,
        );

        tableRows.push(
          <Row key="devices">
            <LabelCell>Devices:</LabelCell>
            <Cell>
              {!authorizedDevicesOverride && (
                <a href={`${manageDevices}&licenseId=${license.id}`}>Manage</a>
              )}
            </Cell>
            <Cell />
          </Row>,
        );

        licenseTableUIArray.push(
          <ProductHeader key={`${license.id}-header`}>
            <IconWrapper>
              <Icon size="40px" />
            </IconWrapper>
            <ProductName>{name}</ProductName>
          </ProductHeader>,
        );

        licenseTableUIArray.push(
          <Table key={license.id} style={tableStyle}>
            {tableRows}
          </Table>,
        );

        if (hasAdditionalLicenses) {
          licenseTableUIArray.push(
            <AdditionalLicensesToggleContainer key={`${license.id}-toggle`}>
              <AdditionalLicensesToggle
                $expanded={isExpanded}
                onClick={() => toggleProductExpansion(productName)}
              >
                {isExpanded ? 'Hide' : 'Show'} {additionalLicenses.length} Additional{' '}
                {additionalLicenses.length === 1 ? 'License' : 'Licenses'}
              </AdditionalLicensesToggle>
            </AdditionalLicensesToggleContainer>,
          );
        }

        // Render additional licenses if expanded
        if (isExpanded && hasAdditionalLicenses) {
          additionalLicenses.forEach((additionalLicense: LicenseEntry) => {
            const {
              id: additionalLicenseId,
              type: additionalType,
              costCenterType: additionalCostCenterType,
              costCenterId: additionalCostCenterId,
              activityState: additionalActivityState,
              authorizedDevicesOverride: additionalAuthorizedDevicesOverride,
            } = additionalLicense;
            const additionalTrialPending =
              additionalActivityState === CostCenter.ACTIVITY_STATES.pending ? ' (Pending)' : '';

            // Get full license info for status calculation
            const additionalFullLicenseInfo = allProductLicenses?.[productName]?.find(
              (lic: FullLicenseInfo & { id: unknown }) => lic.id === additionalLicenseId,
            );
            const additionalLicenseStatus = additionalFullLicenseInfo
              ? calculateLicenseStatus(additionalFullLicenseInfo)
              : null;

            const additionalRows: ReactElement[] = [
              <Row key="cost-center">
                <LabelCell>Cost Center:</LabelCell>
                <Cell>
                  {additionalCostCenterType === 'institutional'
                    ? `${costCenters?.[additionalCostCenterId]?.name || 'N/A'}`
                    : 'Individual'}
                </Cell>
                <Cell />
              </Row>,
              <Row key="owner">
                <LabelCell>Owner:</LabelCell>
                <Cell>
                  {(() => {
                    const costCenter = costCenters?.[additionalCostCenterId];
                    const ownerUserId = costCenter?.owner;
                    const ownerUser = ownerUserId ? users?.[ownerUserId] : undefined;
                    if (ownerUser) {
                      const { firstname, lastname, primaryEmail } = ownerUser;
                      return (
                        `${firstname || ''} ${lastname || ''}`.trim() +
                        (primaryEmail ? ` (${primaryEmail})` : '')
                      );
                    }
                    return 'N/A';
                  })()}
                </Cell>
                <Cell />
              </Row>,
              <Row key="license-type">
                <LabelCell>License Type:</LabelCell>
                <Cell>
                  {additionalType}{' '}
                  <FjTooltip Component={<>Activate by Signing in to {activationProductName}</>}>
                    {` ${additionalTrialPending}`}
                  </FjTooltip>
                </Cell>
                <Cell />
              </Row>,
              <Row key="eula">
                <LabelCell>EULA:</LabelCell>
                <Cell>
                  <a rel="noopener noreferrer" target="_blank" href={eula}>
                    View
                  </a>
                </Cell>
                <Cell />
              </Row>,
              <Row key="status">
                <LabelCell>Status:</LabelCell>
                <Cell span="*">
                  {additionalLicenseStatus?.displayText || 'Unknown'}, but does not apply as this is
                  not the user&apos;s most permissive license for {name}.
                </Cell>
              </Row>,
              <Row key="license-expiration">
                <LabelCell>
                  {additionalCostCenterType === 'institutional'
                    ? 'Group License Expiration:'
                    : 'Expiration:'}
                </LabelCell>
                <Cell>
                  {additionalLicenseStatus?.licenseEndDate
                    ? formatDate(additionalLicenseStatus.licenseEndDate)
                    : 'N/A'}
                </Cell>
                <Cell />
              </Row>,
            ];

            // Only show user seat expiration for institutional licenses
            if (additionalCostCenterType === 'institutional') {
              additionalRows.push(
                <Row key="user-seat-expiration">
                  <LabelCell>User Seat Expiration:</LabelCell>
                  <Cell>
                    {additionalLicenseStatus?.userSeatEndDate
                      ? formatDate(additionalLicenseStatus.userSeatEndDate)
                      : 'not set'}
                  </Cell>
                  <Cell />
                </Row>,
              );
            }

            // Add country row
            additionalRows.push(
              <Row key="country">
                <LabelCell>Country:</LabelCell>
                <Cell>
                  {(() => {
                    const countryCode =
                      additionalCostCenterId && costCenters?.[additionalCostCenterId]?.countryCode;
                    if (!countryCode) return 'N/A';
                    const countryName = FlowjoCommons.Regional.getCountryName(countryCode);
                    return countryName ? `${countryName} (${countryCode})` : countryCode;
                  })()}
                </Cell>
                <Cell />
              </Row>,
            );

            // Add devices row
            additionalRows.push(
              <Row key="devices">
                <LabelCell>Devices:</LabelCell>
                <Cell>
                  {!additionalAuthorizedDevicesOverride && (
                    <a href={`${manageDevices}&licenseId=${additionalLicenseId}`}>Manage</a>
                  )}
                </Cell>
                <Cell />
              </Row>,
            );

            licenseTableUIArray.push(
              <AdditionalLicenseTable
                key={`additional-license-${additionalLicenseId}`}
                style={tableStyle}
              >
                {additionalRows}
              </AdditionalLicenseTable>,
            );
          });
        }
      });
      return licenseTableUIArray;
    }
    return <NoProductsMessage>You don&#39;t have any registered products.</NoProductsMessage>;
  };

  return <Container>{renderProductTables()}</Container>;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: 600px;
  overflow-y: auto;
  width: 100%;

  & > div {
    margin-bottom: 30px;
  }
`;

const ProductHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
  padding-left: 10px;
`;

const IconWrapper = styled.span`
  margin-left: 10px;
`;

const ProductName = styled.span`
  margin-left: 10px;
`;

const LabelCell = styled(Cell)`
  justify-content: flex-end;
`;

const NoProductsMessage = styled.div`
  color: ${(props) => props.theme.colors.selectableDarkBg};
  font-size: ${(props) => props.theme.fontSizes.h3};
  border: dashed 1px ${(props) => props.theme.colors.selectableDarkBg};
  padding: 15px;
`;

const AdditionalLicensesToggleContainer = styled.div`
  display: flex;
  width: 100%;
  margin-top: -20px;
  margin-bottom: 10px;
`;

const AdditionalLicensesToggle = styled.a<{ $expanded?: boolean }>`
  color: ${({ theme }) => theme.colors.selectableLightBg};
  cursor: pointer;
  text-decoration: none;
  font-size: 0.9em;
  display: inline-flex;
  align-items: center;
  margin-left: 10px;
  margin-top: 8px;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
  }

  &::before {
    content: '${({ $expanded }) => ($expanded ? '▼' : '▶')}';
    margin-right: 0.5em;
    font-size: 0.7em;
  }
`;

const AdditionalLicenseTable = styled(Table)`
  margin-top: 8px;
  margin-left: 20px;
  border-left: 3px solid ${(props) => props.theme.colors.selected};
  padding-left: 10px;
  opacity: 0.9;
`;

export default LicenseDrawer;
